import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faXmark } from '@fortawesome/free-solid-svg-icons';
import { scrollToElement } from '../../utils/scrollToElement';
import './styles/Nav.css';

const HOME_SECTIONS = [
    { id: 'about', label: 'About' },
    { id: 'experience', label: 'Experience' },
    { id: 'skills', label: 'Skills' },
    { id: 'services', label: 'Services' },
    { id: 'contact', label: 'Contact' },
];

const Nav = () => {
    const location = useLocation();
    const isHome = location.pathname === '/' || location.pathname === '/home';
    const [menuOpen, setMenuOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('');

    // Close the mobile menu on route change so it doesn't stay open after navigating.
    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    // Close the mobile menu on Escape, or on any click outside the nav, or
    // if the viewport is resized past the mobile breakpoint while it's open.
    useEffect(() => {
        if (!menuOpen) return;

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setMenuOpen(false);
        };

        const onClickOutside = (e: MouseEvent) => {
            if (!(e.target as HTMLElement).closest('.nav')) setMenuOpen(false);
        };

        const onResize = () => {
            if (window.innerWidth > 820) setMenuOpen(false);
        };

        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('click', onClickOutside);
        window.addEventListener('resize', onResize);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('click', onClickOutside);
            window.removeEventListener('resize', onResize);
        };
    }, [menuOpen]);

    // Highlights whichever section is currently under the nav as the user
    // scrolls. Re-queries elements by id on every tick rather than caching
    // refs, since Home is lazy-loaded and its sections may not exist in the
    // DOM yet on the first run of this effect — this self-heals once they do.
    useEffect(() => {
        if (!isHome) {
            setActiveSection('');
            return;
        }

        let ticking = false;

        const updateActiveSection = () => {
            ticking = false;
            const scrollPos = window.scrollY + 96;
            let current = '';
            for (const section of HOME_SECTIONS) {
                const el = document.getElementById(section.id);
                if (el && el.offsetTop <= scrollPos) {
                    current = section.id;
                }
            }
            setActiveSection(current);
        };

        const onScroll = () => {
            if (!ticking) {
                ticking = true;
                requestAnimationFrame(updateActiveSection);
            }
        };

        updateActiveSection();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);

        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
        };
    }, [isHome]);

    // HashRouter already owns window.location.hash for routing, so section
    // links can't be real "/#section" URLs without colliding with it — while
    // on the home page they just scroll; from any other page they fall back
    // to a plain navigation to "/" (landing at the top, not the section).
    const scrollToSection = (id: string) => (e: React.MouseEvent) => {
        if (!isHome) return;
        e.preventDefault();
        scrollToElement(id);
    };

    return (
        <header className="nav">
            <div className="nav__inner container">
                <Link to="/" className="nav__brand" title="Go to the homepage">
                    Sergio Beleño
                </Link>

                <nav className={`nav__links${menuOpen ? ' nav__links--open' : ''}`}>
                    {HOME_SECTIONS.map((section) => (
                        <Link
                            key={section.id}
                            to="/"
                            onClick={scrollToSection(section.id)}
                            className={`nav__link${activeSection === section.id ? ' nav__link--active' : ''}`}
                            aria-current={activeSection === section.id ? 'true' : undefined}
                            title={`Scroll to the ${section.label} section`}
                        >
                            {section.label}
                        </Link>
                    ))}
                    <Link
                        to="/blog"
                        className={`nav__link${location.pathname === '/blog' ? ' nav__link--active' : ''}`}
                        title="Go to the Blog page"
                    >
                        Blog
                    </Link>
                </nav>

                <button
                    className="nav__toggle"
                    aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                    title={menuOpen ? 'Close the navigation menu' : 'Open the navigation menu'}
                    onClick={() => setMenuOpen((open) => !open)}
                >
                    <FontAwesomeIcon icon={menuOpen ? faXmark : faBars} />
                </button>
            </div>
        </header>
    );
};

export default Nav;
